import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Feature, useFeatureFlag } from '@src/components/FeatureFlag'
import { productsByCategorySelector } from '@src/selectors/app'
import { BillingProduct, BillingProductCategory, Product } from '@src/types'

const STELO_CGM_CATEGORY = BillingProductCategory.SteloCgm

export const isSteloCgmTwoAddon = (
  product?: Pick<Product, 'key'> | null,
): product is Pick<Product, 'key'> => {
  return product?.key === BillingProduct.SteloCgm_2
}

export const hasSteloCgmTwoAddon = (products: Array<Pick<Product, 'key'>>) => {
  return products.some((product) => isSteloCgmTwoAddon(product))
}

export const resolveSteloAddonProducts = (products: Product[]) => {
  const oneTimeProductByKey = products.find((product) => product.key === BillingProduct.SteloCgm_2)
  const recurringProductByKey = products.find(
    (product) => product.key === BillingProduct.SteloCgm_2Monthly,
  )

  const recurringProduct = recurringProductByKey || products.find((product) => product.recurring)
  const oneTimeProduct = oneTimeProductByKey || products.find((product) => !product.recurring)

  return { recurringProduct, oneTimeProduct }
}

export const usePreloadSteloAddonProducts = (addons: Product[]) => {
  const dispatch = useDispatch()
  const steloAddonOfferEnabled = useFeatureFlag(Feature.SteloCgmAddonOffer)

  const shouldPreload = steloAddonOfferEnabled && hasSteloCgmTwoAddon(addons)

  useEffect(() => {
    if (!shouldPreload) {
      return
    }

    dispatch({
      type: 'app/fetchProducts',
      payload: { category: STELO_CGM_CATEGORY },
    })
  }, [dispatch, shouldPreload])
}

export const useSteloCgmProducts = () => {
  const dispatch = useDispatch()
  const products = useSelector(productsByCategorySelector(STELO_CGM_CATEGORY))
  const [productsLoaded, setProductsLoaded] = useState(false)

  useEffect(() => {
    let mounted = true

    dispatch({
      type: 'app/fetchProducts',
      payload: { category: STELO_CGM_CATEGORY },
      complete: () => {
        if (mounted) {
          setProductsLoaded(true)
        }
      },
    })

    return () => {
      mounted = false
    }
  }, [dispatch])

  return { products, productsLoaded }
}
